<template>
<section :class="{ 'public-folder': isPublic }">

  <!-- Access banner -->
  <div class="access-banner" :class="isPublic ? 'public' : 'private'">
    <span class="access-label">
      <span :class="isPublic ? 'icon-earth' : 'icon-lock'" />
      <template v-if="isPublic">PUBLIC &mdash; visible to anyone on the internet</template>
      <template v-else>PRIVATE &mdash; login required</template>
    </span>
    <button class="manage-access-button" @click.prevent="openAccessModal">Manage access</button>
  </div>

  <!-- Folder Path -->
  <div class="flex-row flex-align-center flex-justify-center" style="font-weight: bold; font-size: 1.5em;">
    <span class="pathSegment icon-home pa-05" @click="$emit( 'goto', -1 )" />
    <span>/</span>
    <template v-for="(f,idx) in folderPath">
      <div :key="'seg'+f" @click="$emit( 'goto', idx )" class="pathSegment">{{ f }}</div>
      <div v-if="idx + 1 < folderPath.length" :key="'slash'+f" @click="$emit( 'goto', idx )">/</div>
    </template>
  </div>

  <!-- Search -->
  <div id="search">
    Search: <input type="text" v-model="searchText" /><span class="search-button icon-cross" @click="resetSearch" />
  </div>

  <!-- Toolbar -->
  <div id="controls" class="flex-row flex-align-center flex-justify-between">
    <button :class="{ 'disabled' : folderPath.length == 0 }" :disabled="folderPath.length == 0" @click="$emit( 'selectFolder', '..' )">
      <span class="icon-folder-upload">&nbsp;</span>
      <span class="filename">Up a level</span>
    </button>
    <div>
      Order by:
      <button
        :class="{'selected': sortBy === 'filename'}"
        @click.prevent="sort('filename')">Name {{sortBy === 'filename' ? (sortAscending ? '(A-Z)' : '(Z-A)') : ''}}
      </button>
      <button
        :class="{'selected': sortBy === 'size'}"
        @click.prevent="sort('size')">Size {{sortBy === 'size' ? (sortAscending ? '(Small -> Large)' : '(Large -> Small)') : '' }}
      </button>
      <button
        :class="{'selected': sortBy === 'ctime'}"
        @click.prevent="sort('ctime')">Date {{sortBy === 'ctime' ? (sortAscending ? '(Oldest First)' : '(Newest First)') : '' }}
      </button>
    </div>
    <div>
      <span class="icon icon-folder-plus pa-05" @click.prevent="$refs.createFolderModal.open()" />
    </div>
  </div>


  <!-- List of subfolders -->
  <div id="folder-list" v-for="folderName in filteredSubfolders"
    :key="folderName"
    @click="$emit( 'selectFolder', folderName )"
    class="list-item flex-row flex-align-center flex-justify-between"
  >
    <span class="list-item-name">
      <span class="icon-folder">&nbsp;</span>
      <span class="filename">{{ folderName }}</span>
      <span v-if="subfolderAccess[folderName].access === 'public'" class="public-badge"><span class="icon-earth" /> PUBLIC</span>
      <span v-if="subfolderAccess[folderName].managed === true" class="managed-badge"><span class="icon-lock" /> READ ONLY</span>
    </span>

    <span v-if="!isManaged" class="icon-bin deleteButton list-button" @click.stop="showDeleteFolder( folderName )"></span>
  </div>


  <!-- Multi-select tools -->
  <div v-if="files.length" class="multiselect-bar">
    <MultiselectTools
      :numSelected="selectedItems.length"
      :showDelete="true"
      @all="selectAllFiles"
      @none="selectNoneFiles"
      @delete="confirmDeleteSelected"
    >
      <span v-if="canMakePublic" class="pillButton secondary border small flex-row flex-align-center flex-gap-05" @click="makePublicSelected">
        <span class="icon-earth color-orange" /> Make public
      </span>
      <span v-if="canMakePrivate" class="pillButton secondary border small flex-row flex-align-center flex-gap-05" @click="makePrivateSelected">
        <span class="icon-lock" /> Make private
      </span>
    </MultiselectTools>
  </div>


  <!-- List of files -->
  <div id="item-list" v-for="item in files"
    :key="item.filename"
    @click="selectItem(item)"
    class="list-item flex-row flex-align-center flex-justify-between"
    :class="{'selected': selectedItem === item, 'multi-selected': isSelected(item)}">

    <span class="flex-row flex-align-center flex-gap-05" style="flex-grow: 1; min-width: 0;">
      <input
        type="checkbox"
        class="select-checkbox"
        :checked="isSelected(item)"
        @click.stop="toggleSelect(item)" />

      <span v-if="editingItem === item" class="flex-row flex-align-center flex-justify-start flex-gap-1">
        <input
          v-model="newName"
          class="list-item-name"
          @click.stop />

        <button
          class="pointer"
          @click.prevent="renameItem(item)"
          @click.stop>
          save
        </button>
        <span @click="editingItem = null"><span class="icon-cross" /></span>
      </span>

      <span v-else class="list-item-name">
        <span :class="getIconName(item)">&nbsp;</span>
        <span class="filename">{{item.filename}}</span>
        <span v-if="isFilePublic(item)" class="public-badge"><span class="icon-earth" /> PUBLIC</span>
        <span class="fileinfo">{{displaySize(item)}} {{displayDate(item.ctime)}}</span>
        <span v-if="!isManaged && selectedItem === item && editingItem !== item" class="icon-pencil list-icon-button ml-1" @click.stop="editItem(item)"></span>
      </span>
    </span>

    <span v-if="!isManaged" class="icon-bin deleteButton list-button" @click.stop="deleteItem(item)"></span>
    <!-- <div class="reset-float"></div> -->
  </div>


  <StretchModal padding="2em" maxWidth="600px" ref="createFolderModal">
    <template #header>Create New Folder</template>
    <section>
      New Folder Name:<br>
      <input type="text" v-model="newFolderName" placeholder="folder name" class="w-100" @keyup.enter="createFolder()" />
      <div class="flex-row flex-justify-end mt-2"><button class="SquareBlueButton" @click="createFolder()">Create</button></div>
    </section>
  </StretchModal>

  <ConfirmDialog padding="2em" maxWidth="600px" ref="deleteFolderModal" @confirm="deleteFolder()">
    <div v-if="folderToDelete">Really delete folder <b>{{ folderToDelete }}</b>?</div>
  </ConfirmDialog>

  <ConfirmDialog padding="2em" maxWidth="600px" confirm="Delete Files" ref="deleteSelectedModal" @confirm="deleteSelected()">
    <div>Really delete <b>{{ selectedItems.length }}</b> selected file{{ selectedItems.length === 1 ? '' : 's' }}?</div>
    <div class="bold mt-1" style="color: var(--pp10-red); font-size: 1.1em;">This action CAN NOT be undone!</div>
  </ConfirmDialog>

  <FolderAccessModal ref="accessModal" :folderLabel="currentFolderLabel" @save="saveAccess" />

</section>
</template>



<script>
// import LibUI from '@/libraries/LibUI';
import ConfirmDialog from '@/components/utilities/ConfirmDialog.vue'
import StretchModal from '@/components/utilities/StretchModal.vue'
import FolderAccessModal from '@/components/FolderAccessModal.vue'
import MultiselectTools from '@/components/utilities/MultiselectTools.vue'

export default {
  name: 'media-library-list',

  components: {
    ConfirmDialog,
    StretchModal,
    FolderAccessModal,
    MultiselectTools,
  },


  props: {
    subfolders: Array,
    items: Array,
    selectNewest: Number,
    update: Number,
    folderPath: {
      type: Array,
      default: () => []
    },

    // Access record for the current folder: { access: 'public'|'private', grants: [], managed: false }
    folderAccess: {
      type: Object,
      default: () => ({ access: 'private', grants: [], managed: false })
    },

    // Map of { [subfolderName]: {access: 'public' | 'private', managed: boolean } for badging child folders.
    subfolderAccess: {
      type: Object,
      default: () => ({})
    },

    // Bare filenames explicitly made public in this (private) folder.
    publicFiles: {
      type: Array,
      default: () => []
    },
  },


  data() {
    return {
      files: [],

      selectedItem: null,
      selectedItems: [],
      editingItem: null,
      newName: "",

      newFolderName: null,
      folderToDelete: null,

      sortBy: "",
      sortAscending: true,

      searchText: "",
      filteredItems: [],
      filteredSubfolders: [],
    }
  },



  created(){
    this.files = this.items
    this.filteredSubfolders = this.subfolders
  },



  computed: {
    isPublic() {
      return this.folderAccess && this.folderAccess.access === 'public'
    },

    isManaged() {
      return this.folderAccess && this.folderAccess.managed === true
    },

    currentFolderLabel() {
      if( !this.folderPath || this.folderPath.length === 0 ) return 'Home'
      return this.folderPath[ this.folderPath.length - 1 ]
    },

    // Per-file public overrides only make sense inside a private folder.
    // "Make public" is offered when a selected, non-dotfile file isn't already public.
    canMakePublic() {
      return !this.isPublic && this.selectedItems.some( i => !this.isDotfile( i ) && !this.publicFiles.includes( i.filename ) )
    },

    // "Make private" is offered when a selected file is currently a public override.
    canMakePrivate() {
      return !this.isPublic && this.selectedItems.some( i => this.publicFiles.includes( i.filename ) )
    },
  },



  watch: {
    selectNewest(){
      if(this.selectNewest === 0) return;

      let newestFile = this.items[0];
      for(let file of this.items){
        if(file.ctime > newestFile.ctime) newestFile = file;
      }

      this.selectItem(newestFile);
    },

    searchText(){
      this.updateSearch();
    },

    update(){
      this.updateSearch();
    },

    items() {
      if(this.searchText === '') this.files = this.items
      this.selectNoneFiles()   // stale selections after a refresh/navigation
    },
    subfolders() { if( this.searchText === '' ) this.filteredSubfolders = this.subfolders }
  },



  methods: {
    deleteItem(item){
      this.$emit('delete', item);
    },


    // ---- Multi-select ----

    isDotfile( item ) {
      return !!item && !!item.filename && item.filename.startsWith('.')
    },

    // A file shows the PUBLIC badge when it's a per-file override in a private
    // folder. (Inside a public folder everything is public, so no per-file badge.)
    isFilePublic( item ) {
      return !this.isPublic && this.publicFiles.includes( item.filename )
    },

    isSelected( item ) {
      return this.selectedItems.includes( item )
    },

    toggleSelect( item ) {
      if( this.isSelected( item ) ) this.selectedItems = this.selectedItems.filter( i => i !== item )
      else this.selectedItems = [ ...this.selectedItems, item ]
    },

    selectAllFiles() {
      this.selectedItems = [ ...this.files ]
    },

    selectNoneFiles() {
      this.selectedItems = []
    },

    // Add selected (non-dotfile) files to the public-override list and emit the
    // full desired list — the server does a full-list replace.
    makePublicSelected() {
      const names = new Set( this.publicFiles )
      for( const item of this.selectedItems ) {
        if( !this.isDotfile( item ) ) names.add( item.filename )
      }
      this.$emit( 'setPublicFiles', [ ...names ] )
      this.selectNoneFiles()
    },

    // Remove selected files from the public-override list.
    makePrivateSelected() {
      const remove = new Set( this.selectedItems.map( i => i.filename ) )
      this.$emit( 'setPublicFiles', this.publicFiles.filter( n => !remove.has( n ) ) )
      this.selectNoneFiles()
    },

    confirmDeleteSelected() {
      if( !this.selectedItems.length ) return
      this.$refs.deleteSelectedModal.open()
    },

    deleteSelected() {
      this.$emit( 'deleteItems', [ ...this.selectedItems ] )
      this.selectNoneFiles()
    },

    resetSearch(){
      this.searchText = '';
    },

    updateSearch(){
      if(this.searchText === '') {
        this.files = this.items
        this.filteredSubfolders = this.subfolders
        return
      }

      this.filteredItems = this.items.filter(item => {
        if(item.filename.toLowerCase().includes(this.searchText.toLowerCase())) return true;
        return false;
      });

      this.files = this.filteredItems;

      this.filteredSubfolders = this.subfolders.filter( folder => {
        if( folder.toLowerCase().includes( this.searchText.toLocaleLowerCase() ) ) return true
      })
    },

    selectItem(item) {
      if(item === this.selectedItem) {
        this.selectedItem = null;
        this.$emit('select', null);
        return;
      }

      this.editingItem = null;
      this.selectedItem = item;
      this.newName = item.filename;
      this.$emit('select', item);
    },

    editItem(item){
      this.editingItem = item;
    },

    renameItem(item){
      if( item.filename === this.newName ) {
        this.editingItem = null
        return
      }

      this.$emit( 'renameItem', item.filename, this.newName );
    },

    displaySize(file){
      if(file.size > 999 && file.size <= 999999 ) return Math.round(file.size/1000) + " KB";
      else if(file.size > 999999 && file.size <= 999999999) return Math.round(file.size/1000000) + " MB";
      else return Math.round(file.size/1000000000) + " GB";
    },

    displayDate(timestamp){
      let date = new Date(timestamp*1000);
      return date.toLocaleString()
    },

    sort(attrName){
      if(this.sortBy === attrName) this.sortAscending = !this.sortAscending;
      else if(attrName === 'ctime') this.sortAscending = false; // default NEWEST first for date
      else if(attrName === 'size') this.sortAscending = false // default LARGEST first for size
      else this.sortAscending = true; // default A - Z for alphabetical

      let neg = -1, pos = 1;
      if(!this.sortAscending){
        neg = 1;
        pos = -1;
      }
      this.items.sort((a,b) => {
        if(typeof a[attrName] === 'string'){
          let compareVal = a[attrName].toLowerCase().localeCompare(b[attrName].toLowerCase());
          if(compareVal < 0) return neg;
          if(compareVal === 0) return 0;
          else return pos;
        }else{
          if( a[attrName] < b[attrName] ) return neg;
          if( a[attrName] === b[attrName] ) return 0;
          else return pos;
        }

      });

      this.sortBy = attrName;
    },

    getIconName( file ) {
      if( !file || !file.filename ) return 'icon-file-empty'

      const fn = file.filename
      if( fn.endsWith('.png') || fn.endsWith('.jpg') || fn.endsWith('.jpeg') || fn.endsWith('.gif') || fn.endsWith('.jxl') ) return 'icon-file-picture'
      if( fn.endsWith('.mp4') || fn.endsWith('.m4v') || fn.endsWith('.mov') ) return 'icon-file-play'
      return 'icon-file-empty'
    },



    createFolder() {
      if( !this.newFolderName ) return
      this.$emit( 'createFolder', this.newFolderName )
      this.newFolderName = null
      this.$refs.createFolderModal.close()
    },

    showDeleteFolder( folder ) {
      this.folderToDelete = folder
      this.$refs.deleteFolderModal.open()
    },

    deleteFolder() {
      if( !this.folderToDelete ) return
      this.$emit( 'deleteFolder', this.folderToDelete )
      this.folderToDelete = null
    },


    openAccessModal() {
      const access = this.folderAccess ? this.folderAccess.access : 'private'
      const grants = this.folderAccess ? this.folderAccess.grants : []
      this.$refs.accessModal.open( access, grants )
    },

    saveAccess( { access, grants } ) {
      this.$emit( 'saveAccess', access, grants, () => this.$refs.accessModal.close() )
    },

  }
}
</script>

<style scoped>

/* ---- Access control ---- */

/* Ambient danger cue: subtle orange frame around the whole browser when public */
section.public-folder {
  border: 2px solid var(--pp10-orange);
  border-radius: 0.5em;
  box-shadow: inset 0 0 0 4px var(--pp10-orange-95);
}

.access-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4em 0.75em;
  margin-bottom: 0.5em;
  border-radius: 0.25em;
  font-weight: bold;
}
.access-banner.private {
  background-color: var(--ekno-blue-95);
  color: var(--ekno-blue);
}
.access-banner.public {
  background-color: var(--pp10-orange);
  color: white;
  letter-spacing: 0.02em;
}
.access-label { display: inline-flex; align-items: center; gap: 0.4em; }

.manage-access-button {
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.4em;
  padding: 0.35em 0.75em;
  cursor: pointer;
  font-weight: bold;
  color: #333;
}
.manage-access-button:hover { background: white; }

.public-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25em;
  background-color: var(--pp10-orange);
  color: white;
  font-size: 0.7em;
  font-weight: bold;
  letter-spacing: 0.03em;
  border-radius: 1em;
  padding: 0.15em 0.6em;
  margin-left: 0.5em;
  vertical-align: middle;
}
.managed-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25em;
  background-color: var(--pp10-red);
  color: white;
  font-size: 0.7em;
  font-weight: bold;
  letter-spacing: 0.03em;
  border-radius: 1em;
  padding: 0.15em 0.6em;
  margin-left: 0.5em;
  vertical-align: middle;
}

.multiselect-bar {
  padding: 0.25em 0.25em 0.5em;
}

.select-checkbox {
  cursor: pointer;
  width: 1.1em;
  height: 1.1em;
  flex-shrink: 0;
}

.list-item.multi-selected {
  background-color: var(--ekno-blue-95);
  box-shadow: inset 3px 0 0 var(--ekno-blue);
}

.pathSegment {
  background-color: var(--pp10-gray-90);
  border-radius: 0.25em;
  padding: 0.25rem 0.5rem;
  margin: 0 0.5rem;
  cursor: pointer;
}

.pathSegment:hover {
  background-color: var(--ekno-blue-90);
}

/* See AdminViews.css */
.filename {
  margin-right: 1em;
}
.fileinfo {
  padding: 0.3em 0;
  color: #555;
  font-size: 0.8em;
}

#search {
  margin: 0.5em 0.25em;
}

.search-button {
  background-color: white;
  padding: 0.25em;
  margin: 0.25em;
  color: darkred;
  border: 1px solid lightgray;
}
.search-button:hover {
  color: red;
  border: 1px solid #ccc;
}

#controls {
  background-color: #eee;
  padding: 0.5em 0.25em;
  margin: 0;
}
#controls button {
  padding: 0.5em;
  margin: 0.25em;
  border: 1px gray solid;
  border-radius: 0.5em;
  cursor: pointer;
}
#controls button:hover {
  background: white;
}
#controls button.selected {
  background-color: #4E98FF;
  color: #FFF;
  font-weight: bold;
}
#controls button.selected:hover{
  background: #5EA8FF;
}
#controls button.disabled {
  background-color: inherit;
  cursor: not-allowed;
}

#controls .icon:hover {
  color: var(--pp10-green);
}


</style>
